import { InternalCapability } from '../data/account';
import Communication from '../utils/Communication';
import Logger from '../utils/Logger';
import Session from '../utils/Session';
import Util from '../utils/Util';
import { ChatbotTriggers } from './ChatbotMessengerState.enum';
import {
	markChatClosed,
	markChatOpen,
	wasChatOpenBeforeRefresh,
} from './sierraChatCookie';

const SIERRA_SDK_BASE_URL = 'https://sierra.chat/agent';

const STAMPS_LOGO_SVG =
	'<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M26.9787 9.26848V5.0656H22.7758C22.7758 6.26176 21.7851 7.25248 20.5889 7.25248C19.3927 7.25248 18.402 6.26176 18.402 5.0656H13.6865C13.6865 6.26176 12.6958 7.25248 11.4996 7.25248C10.3035 7.25248 9.31274 6.26176 9.31274 5.0656H5.10986V9.26848C6.30602 9.26848 7.29674 10.2592 7.29674 11.4554C7.29674 12.6515 6.30602 13.6422 5.10986 13.6422V18.3578C6.30602 18.3578 7.29674 19.3485 7.29674 20.5446C7.29674 21.7408 6.30602 22.7315 5.10986 22.7315V26.9344H9.31274C9.31274 25.7382 10.3035 24.7475 11.4996 24.7475C12.6958 24.7475 13.6865 25.7382 13.6865 26.9344H18.402C18.402 25.7382 19.3927 24.7475 20.5889 24.7475C21.7851 24.7475 22.7758 25.7382 22.7758 26.9344H26.9787V22.7315C25.7825 22.7315 24.7918 21.7408 24.7918 20.5446C24.7918 19.3485 25.7825 18.3578 26.9787 18.3578V13.6422C25.7825 13.6422 24.7918 12.6515 24.7918 11.4554C24.7918 10.2592 25.7825 9.26848 26.9787 9.26848ZM21.7505 21.7062H10.338V10.2938H21.7505V21.7062Z" fill="#FF0000"/></svg>';

// Served as a static asset via webpack CopyPlugin (stable path, no content hash).
// __webpack_public_path__ is a webpack runtime variable — evaluated lazily to avoid
// ReferenceError in Vitest which does not define it.
const getStampsLogoAvatarURL = () => {
	const publicPath =
		typeof __webpack_public_path__ !== 'undefined'
			? __webpack_public_path__
			: '/';
	return `${window.location.origin}${publicPath}assets/images/stamps-logo-square.svg`;
};

let sierraScript = null;
let isSierraReady = false;
let sierraQueue = [];
let conversationEnded = false;

const flushQueue = () => {
	isSierraReady = true;
	sierraQueue.forEach(fn => fn());
	sierraQueue = [];
};

const drainQueueOnError = () => {
	sierraScript = null;
	isSierraReady = false;
	sierraQueue.forEach(fn => fn());
	sierraQueue = [];
};

const callSierra = fn => {
	if (isSierraReady && window.sierra) {
		fn();
	} else {
		sierraQueue.push(fn);
	}
};

// Sierra requires YYYY/MM/DD; Util.formatDate returns YYYY-MM-DD
const formatSierraDate = date => {
	if (!date) return '';
	try {
		return Util.formatDate(date).replace(/-/g, '/');
	} catch {
		return '';
	}
};

// Mirrors the plan-level map from the new React app (metadata-helpers.ts).
// Keys are plan IDs, values are lowercase plan name strings for Sierra.
const PLAN_LEVEL_MAP = {
	// GBB plans
	380: 'good',
	381: 'better',
	383: 'best',
	// New lineup 2026
	401: 'trial',
	402: 'basic',
	403: 'professional',
	404: 'multilocation1',
	405: 'multilocation2',
	406: 'multilocation3',
	// Legacy plans
	375: 'core',
	376: 'office',
	377: 'ecommerce',
	378: 'essential',
	382: 'payg',
};

const getPlanLevelFromId = planId =>
	(planId && PLAN_LEVEL_MAP[planId]) || 'unknown';

const getMessagingOrigin = trigger => {
	if (trigger === ChatbotTriggers.CancelAccount) {
		return 'brightback';
	}
	return 'app';
};

const buildMetadata = async (trigger, page) => {
	const accountInfo = Session.getAccountInfo();
	const internalAccountInfo = Session.getInternalAccountInfo();

	const {
		customerId,
		userId,
		corporationId,
		planDetails = {},
		meterDetails = {},
		customerDetails = {},
		internalAccountDetails = {},
	} = accountInfo;

	const { username, creationDate, address = {} } = customerDetails;
	const { planId } = planDetails;
	const { meterNumber } = meterDetails;
	const { internalCapabilities = [] } = internalAccountDetails;

	const userAdmin = internalCapabilities.includes(
		InternalCapability.ManageUsers
	)
		? 'true'
		: 'false';

	const billingCycleDate = formatSierraDate(
		Util.getBillingDate(internalAccountInfo.BillingDayOfMonth)
	);
	const startDate = formatSierraDate(
		creationDate ? Util.convertDate(new Date(creationDate)) : null
	);

	let requesterEmail = '';
	let planPrice = '';

	try {
		const [contactResponse, billingResponse] = await Promise.all([
			Communication.getUserContactInfo(),
			Communication.billingPlans(),
		]);

		if (Communication.isSuccessful(contactResponse)) {
			requesterEmail = contactResponse?.data?.UserInfo?.EmailAddress || '';
		}

		if (Communication.isSuccessful(billingResponse)) {
			const { BillingPlans = [], CurrentPlanSku } = billingResponse.data;
			const currentPlan = BillingPlans.find(p => p.Sku === CurrentPlanSku);
			if (currentPlan) {
				planPrice = currentPlan.Price?.toString() || '';
			}
		}
	} catch {
		// non-fatal — leave empty
	}

	const metadata = {
		requesterName: address.name || '',
		requesterEmail,
		username: username || '',
		brand: 'stamps',
		sellerId: customerId?.toString() || '',
		organizationIdSeller: corporationId?.toString() || '',
		planId: planId?.toString() || '',
		planLevel: getPlanLevelFromId(planId),
		planPrice,
		userAdmin,
		billingCycleDate,
		startDate,
		firstPrintDate: '', // not available in session data
		meterId: meterNumber?.toString() || '',
		bridgeId: userId?.toString() || '',
		appVersion: 'legacy_react',
		messagingOrigin: getMessagingOrigin(trigger),
		currentPage: page || '',
	};

	// Add enterpriseCorpId only if it has a value
	if (corporationId != null && corporationId !== 0) {
		metadata.enterpriseCorpId = corporationId.toString();
	}

	return metadata;
};

// Checks for ?openChat=true in the URL hash and auto-opens the chat window.
// Removes the param after handling to prevent re-opening on refresh.
const handleDeepLink = () => {
	const hash = window.location.hash;
	const hashParts = hash.split('?');
	if (hashParts.length < 2) return;

	const searchParams = new URLSearchParams(hashParts[1]);
	if (searchParams.get('openChat') !== 'true') return;

	if (window.sierra?.openChatModal) {
		window.sierra.openChatModal();
	}

	searchParams.delete('openChat');
	const newSearch = searchParams.toString();
	window.history.replaceState(
		{},
		'',
		hashParts[0] + (newSearch ? `?${newSearch}` : '')
	);
};

const injectScript = async (agentToken, targetEnvironment, trigger, page) => {
	if (sierraScript) {
		return;
	}

	// Claim the slot synchronously before any await so concurrent calls see it
	// immediately and don't race to inject a second script tag.
	sierraScript = true;

	const variables = await buildMetadata(trigger, page);

	window.sierraConfig = {
		display: 'corner',
		persistence: 'cookie',
		cookieDomain: '.stamps.com',
		hideOnClose: true,
		canEndConversation: true,
		titleBarEndConversationButton: true,
		inactivityTimeoutSeconds: 7200,
		logoSVG: STAMPS_LOGO_SVG,
		showAvatars: true,
		agentAvatarURL: getStampsLogoAvatarURL(),
		chatInterfaceStrings: {
			name: 'Stamps.com',
			disclosure:
				'By starting a chat with our artificial intelligence-powered assistant, you agree to the automated processing of your personal data.',
		},
		pinDisclosure: true,
		variables,
		colors: {
			titleBar: '#11003a',
			titleBarText: '#ffffff',
			primary: '#11003a',
			userMessage: '#f0f2f5',
			background: '#ffffff',
			disclosure: '#000000',
		},
		typography: {
			fontFamily: 'Figtree, Arial, sans-serif',
		},
		onLoad: () => {
			flushQueue();
			handleDeepLink();
			if (wasChatOpenBeforeRefresh()) {
				window.sierra?.openChatModal();
			}
		},
		onOpen: () => {
			markChatOpen();
			if (conversationEnded) {
				window.sierra.clearPersistedConversation?.();
				conversationEnded = false;
			}
		},
		onClose: () => {
			// Do NOT call markChatClosed() here - the conversation is still active,
			// just minimized. Launcher should remain visible for quick re-access.
		},
		onConversationEnd: () => {
			conversationEnded = true;
			markChatClosed();
			// Force Sierra to end the persisted conversation, then close the modal
			window.sierra?.endPersistedConversation?.();
			window.sierra?.closeChatModal();
		},
	};

	sierraScript = document.createElement('script');
	sierraScript.type = 'module';
	sierraScript.src = `${SIERRA_SDK_BASE_URL}/${agentToken}/embed/${targetEnvironment}`;
	sierraScript.async = true;
	sierraScript.onerror = () => drainQueueOnError();
	document.head.appendChild(sierraScript);
};

const getSessionConfig = () => {
	const sessionData = Session.getData();

	if (sessionData.isSignedIn !== true) {
		return null;
	}

	const { SierraAgentToken, SierraTargetEnvironment, IsInWinClient } =
		Session.getSettings();

	if (!SierraAgentToken || IsInWinClient) {
		return null;
	}

	if (!SierraTargetEnvironment) {
		Logger.logToServer({
			category: 'sierra_init',
			page: 'sierra_chat',
			action: 'missing_target_environment',
		});
		return null;
	}

	return {
		agentToken: SierraAgentToken,
		targetEnvironment: SierraTargetEnvironment,
	};
};

export default class SierraInitializer {
	/**
	 * Silently inject the Sierra SDK script on login.
	 * Does not open the chat modal.
	 * @param {string} [page] - Current page identifier for Sierra metadata
	 */
	static initialize = (page = '') => {
		const config = getSessionConfig();

		if (!config) {
			return;
		}

		injectScript(config.agentToken, config.targetEnvironment, null, page);
	};

	/**
	 * Open the Sierra chat modal, initializing the SDK if needed.
	 * @param {function} [onUnavailable] - Called when Sierra is unavailable
	 * @param {string} [trigger] - ChatbotTriggers value (e.g. CancelAccount for Brightback flow)
	 * @param {string} [page] - Current page identifier for Sierra metadata
	 */
	static openChat = (onUnavailable, trigger, page = '') => {
		const config = getSessionConfig();

		if (!config) {
			if (onUnavailable) {
				onUnavailable();
			}
			return;
		}

		injectScript(config.agentToken, config.targetEnvironment, trigger, page);
		callSierra(() => {
			if (window.sierra?.openChatModal) {
				window.sierra.openChatModal();
			} else if (onUnavailable) {
				onUnavailable();
			}
		});
	};

	/**
	 * Clean up Sierra SDK on sign-out.
	 * Does NOT clear the sierra_chat_open flag - if chat was open during logout,
	 * it will auto-reopen after login to maintain conversation continuity
	 * (important for customer service scenarios where user is instructed to logout/login).
	 * Clears persisted conversation so next login starts fresh.
	 */
	static logout = () => {
		isSierraReady = false;
		sierraQueue = [];
		conversationEnded = false;
		if (window.sierra?.clearPersistedConversation) {
			window.sierra.clearPersistedConversation();
		}
		if (window.sierra?.cleanup) {
			window.sierra.cleanup();
		}
		if (sierraScript && sierraScript.parentNode) {
			sierraScript.parentNode.removeChild(sierraScript);
		}
		sierraScript = null;
	};
}
